export interface DashboardTutorial {
  body: string;
  ctaText: string;
  ctaHref: string;
  assetDownloadHref: string;
  title: string;
  thumbnailSrc: string;
  videoSrc: string;
}

export const TUTORIAL_SEARCH_PARAM = 'tutorial';
export const TUTORIAL_SEARCH_VALUE = 'true';
export const TUTORIAL_ASSET_DOWNLOAD_FILENAME = 'cartwheel-asset.mp4';

export function downloadTutorialAssets(assetDownloadHref: string): void {
  const link = document.createElement('a');
  link.href = assetDownloadHref;
  link.download = TUTORIAL_ASSET_DOWNLOAD_FILENAME;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

const COMIC_TUTORIAL_HREF = `/comic?${TUTORIAL_SEARCH_PARAM}=${TUTORIAL_SEARCH_VALUE}`;
const COMIC_TUTORIAL_CTA = {
  ctaText: 'Get started with Comic',
  ctaHref: COMIC_TUTORIAL_HREF,
};

const APP_TUTORIALS_CDN_BASE =
  'https://resources.getcartwheel.com/share/app-tutorials';

function tutorialAssets(folder: string) {
  return {
    thumbnailSrc: `${APP_TUTORIALS_CDN_BASE}/${folder}/thumbnail.webm`,
    videoSrc: `${APP_TUTORIALS_CDN_BASE}/${folder}/video.webm`,
    assetDownloadHref: `${APP_TUTORIALS_CDN_BASE}/${folder}/${TUTORIAL_ASSET_DOWNLOAD_FILENAME}`,
  };
}

const CINEMATICS_AND_MARKETING_ASSETS = tutorialAssets(
  'cinematics-and-marketing',
);
const INTERACTIVE_PERSONAL_AND_OTHER_ASSETS = tutorialAssets(
  'interactive-personal-and-other',
);

const VIDEO_GAMES_INTERACTIVE_TUTORIAL: DashboardTutorial = {
  title: 'From video to game-ready in 5 minutes',
  body: 'Capture motion from a video, retarget it to your character, and export to your engine.',
  ...COMIC_TUTORIAL_CTA,
  ...INTERACTIVE_PERSONAL_AND_OTHER_ASSETS,
};

const TV_MOVIES_CINEMATICS_TUTORIAL: DashboardTutorial = {
  title: 'Capture your first performance in 5 minutes',
  body: 'Body and face capture from video, refined and exported to your DCC.',
  ...COMIC_TUTORIAL_CTA,
  ...CINEMATICS_AND_MARKETING_ASSETS,
};

const BRAND_MARKETING_TUTORIAL: DashboardTutorial = {
  title: 'Your brand characters in motion in 5 minutes',
  body: 'Capture motion from a video, retarget to your brand character, and export to your finishing system.',
  ...COMIC_TUTORIAL_CTA,
  ...CINEMATICS_AND_MARKETING_ASSETS,
};

const PERSONAL_INDIE_TUTORIAL: DashboardTutorial = {
  title: 'Get your first motion exported in 5 minutes',
  body: 'Capture motion, retarget it, and bring it into your tool of choice.',
  ...COMIC_TUTORIAL_CTA,
  ...INTERACTIVE_PERSONAL_AND_OTHER_ASSETS,
};

const DEFAULT_TUTORIAL: DashboardTutorial = {
  title: 'Capture motion from video in under 5 minutes',
  body: 'Comic turns any video into clean motion data, ready for your pipeline.',
  ...COMIC_TUTORIAL_CTA,
  ...INTERACTIVE_PERSONAL_AND_OTHER_ASSETS,
};

const TUTORIALS_BY_INDUSTRY_FOCUS: Record<string, DashboardTutorial> = {
  VIDEO_GAMES_INTERACTIVE: VIDEO_GAMES_INTERACTIVE_TUTORIAL,
  TV_MOVIES_CINEMATICS: TV_MOVIES_CINEMATICS_TUTORIAL,
  BRAND_MARKETING: BRAND_MARKETING_TUTORIAL,
  PERSONAL_INDIE: PERSONAL_INDIE_TUTORIAL,
  OTHER: DEFAULT_TUTORIAL,
};

export function getTutorialForIndustryFocus(
  industryFocus?: string,
): DashboardTutorial {
  if (!industryFocus) {
    return DEFAULT_TUTORIAL;
  }

  return TUTORIALS_BY_INDUSTRY_FOCUS[industryFocus] ?? DEFAULT_TUTORIAL;
}

export function getTutorialTypeForIndustryFocus(
  industryFocus?: string,
): string {
  if (industryFocus && industryFocus in TUTORIALS_BY_INDUSTRY_FOCUS) {
    return industryFocus;
  }

  return 'OTHER';
}
